import { FormNode, FormEdge } from './api/aboboApiTypes';
import type {
  PatientChartAttributePayload,
  ResponsesPayload,
  ResponseValue,
} from '../components/SharedTypes';
import { addDays, addMonths, addWeeks, addYears, subDays, subMonths, subWeeks, subYears } from 'date-fns';
import * as Sentry from '@sentry/react';

export type FormWizardData = Record<string, ResponseValue[]>;

// Any other node type is walked through, so a form published with a newer node
// type never dead-ends a patient behind the year-long form cache.
export const RENDERABLE_NODE_TYPES: readonly string[] = ['question', 'information'];

export const isRenderableNode = (node: FormNode | null | undefined): boolean =>
  !!node && RENDERABLE_NODE_TYPES.includes(node.nodeType);

const MAX_PASS_THROUGH_CHAIN = 50;

export interface TraversalStep {
  node: FormNode | null;
  actionKeys: string[];
}

const nodeById = (nodes: FormNode[], id: string | undefined): FormNode | null =>
  (id ? nodes.find((node) => node.id === id) : undefined) || null;

// A condition reached by passing through is not a branch of the question being
// left, so it is never evaluated.
const chaseToRenderableNode = (
  startNode: FormNode | null,
  nodes: FormNode[],
  edges: FormEdge[]
): TraversalStep => {
  const actionKeys: string[] = [];
  const seen = new Set<string>();
  let node = startNode;

  while (node && !isRenderableNode(node)) {
    if (seen.has(node.id) || seen.size >= MAX_PASS_THROUGH_CHAIN) {
      return { node: null, actionKeys };
    }
    seen.add(node.id);

    if (node.nodeType === 'action' && node.nodeKey) actionKeys.push(node.nodeKey);

    const currentId = node.id;
    const outgoing = edges.find((edge) => edge.sourceNodeId === currentId);
    node = outgoing ? nodeById(nodes, outgoing.targetNodeId) : null;
  }

  return { node, actionKeys };
};

export const advanceFromNode = (
  currentNode: FormNode | null,
  nodes: FormNode[],
  edges: FormEdge[],
  formData: FormWizardData
): TraversalStep => {
  if (!currentNode) {
    // Find the first node (root node with no incoming edges)
    const firstNode =
      nodes.find((node) => {
        return !edges.some((edge) => edge.targetNodeId === node.id);
      }) || null;
    return chaseToRenderableNode(firstNode, nodes, edges);
  }

  const input = formData[currentNode.id];
  const userInput = input?.at(0)?.data;
  const currentEdges = edges.filter((edge) => edge.sourceNodeId === currentNode.id);

  const defaultNextEdge = currentEdges.find((edge) => {
    return nodeById(nodes, edge.targetNodeId)?.nodeType !== 'condition';
  });

  // Check conditional edges first
  for (const edge of currentEdges) {
    if (edge.id === defaultNextEdge?.id) continue;

    const conditionalNode = nodeById(nodes, edge.targetNodeId);
    if (conditionalNode?.nodeType === 'condition') {
      const conditionData = conditionalNode.metadata?.condition;
      if (!conditionData) continue;

      // Check if this conditional node is for the current node
      // Support both parent_node_id (from JSON) and parentNodeId (from TypeScript)
      const parentNodeId =
        (conditionData as { parent_node_id?: string }).parent_node_id || conditionData.parentNodeId;
      if (parentNodeId === currentNode.id) {
        const { value, operator } = conditionData;

        // Only evaluate condition if user has provided input
        if (userInput && evaluateCondition(currentNode, value, operator, userInput)) {
          const nextEdge = edges.find((candidate) => candidate.sourceNodeId === conditionalNode.id);
          if (nextEdge) {
            return chaseToRenderableNode(nodeById(nodes, nextEdge.targetNodeId), nodes, edges);
          }
        }
      }
    }
  }

  // Return default next node
  if (defaultNextEdge) {
    return chaseToRenderableNode(nodeById(nodes, defaultNextEdge.targetNodeId), nodes, edges);
  }

  return { node: null, actionKeys: [] };
};

export const findNextNode = (
  currentNode: FormNode | null,
  nodes: FormNode[],
  edges: FormEdge[],
  formData: FormWizardData
): FormNode | null => {
  return advanceFromNode(currentNode, nodes, edges, formData).node;
};

export interface CollectedFormActions {
  triggeredActionKeys: string[];
  annotationsByNodeId: Record<string, string[]>;
}

// Replays the final answers, so a branch the patient backed out of fires
// nothing. An annotation marks the last question before it, never an
// information node.
export const collectFormActions = (
  nodes: FormNode[],
  edges: FormEdge[],
  formData: FormWizardData
): CollectedFormActions => {
  const triggeredActionKeys: string[] = [];
  const annotationsByNodeId: Record<string, string[]> = {};
  const reportedKeys = new Set<string>();
  const visitedNodeIds = new Set<string>();
  let boundQuestionId: string | null = null;

  const record = (actionKeys: string[]) => {
    actionKeys.forEach((key) => {
      if (!reportedKeys.has(key)) {
        reportedKeys.add(key);
        triggeredActionKeys.push(key);
      }

      if (!boundQuestionId) return;

      const bound = annotationsByNodeId[boundQuestionId] || [];
      if (!bound.includes(key)) bound.push(key);
      annotationsByNodeId[boundQuestionId] = bound;
    });
  };

  let step = advanceFromNode(null, nodes, edges, formData);
  record(step.actionKeys);

  while (step.node && !visitedNodeIds.has(step.node.id)) {
    visitedNodeIds.add(step.node.id);
    if (step.node.nodeType === 'question') boundQuestionId = step.node.id;

    step = advanceFromNode(step.node, nodes, edges, formData);
    record(step.actionKeys);
  }

  return { triggeredActionKeys, annotationsByNodeId };
};

export interface BuiltResponses {
  responses: ResponsesPayload[];
  patientChartAttributes: PatientChartAttributePayload[];
}

// A skipped question keeps an entry only when an action marked it, so the
// operator can see which question the mark belongs to.
export const buildResponsePayloads = (
  visitedNodeIds: string[],
  nodesById: Record<string, FormNode>,
  formData: FormWizardData,
  annotationsByNodeId: Record<string, string[]>
): BuiltResponses => {
  const responses: ResponsesPayload[] = [];
  const patientChartAttributes: PatientChartAttributePayload[] = [];

  visitedNodeIds.forEach((nodeId) => {
    const node = nodesById[nodeId];
    if (!node) return;

    let values = formData[nodeId] || [];

    if (node.nodeType === 'information' && values.length === 0) {
      values = [{ data: 'No Response Needed' }];
    }

    const annotations = annotationsByNodeId[nodeId];
    const annotated = !!annotations?.length;
    if (values.length === 0 && !annotated) return;

    const responsePayload: ResponsesPayload = {
      questionId: nodeId,
      // Stamp the durable node_key so report templates resolve this
      // answer across revisions without body-text matching.
      nodeKey: node.nodeKey ?? undefined,
      values,
      questionText: node.body,
      questionType: node.nodeSubtype || node.nodeType || 'unknown',
      questionHelperText: node.helperText,
    };

    if (node.originalBody) {
      responsePayload.originalQuestionText = node.originalBody;
    }
    if (node.originalHelperText) {
      responsePayload.originalQuestionHelperText = node.originalHelperText;
    }

    if (annotated) {
      responsePayload.annotations = annotations;
    }

    responses.push(responsePayload);

    if (
      node.chartAttributeId &&
      node.nodeType !== 'information' &&
      values.length > 0
    ) {
      patientChartAttributes.push({
        chartAttributeId: node.chartAttributeId,
        value: values.map((v) => v.data).join(', '),
      });
    }
  });

  return { responses, patientChartAttributes };
};

export const evaluateCondition = (
  sourceNode: FormNode,
  conditionValue: string,
  operator: string,
  userInput: string
): boolean => {
  if (!userInput) return false;

  switch (sourceNode.nodeSubtype) {
    case 'date':
      return evaluateDateCondition(conditionValue, operator, userInput);
    case 'numeric':
      return evaluateNumericCondition(conditionValue, operator, userInput);
    case 'multiple_choice':
      return evaluateMultipleChoiceCondition(sourceNode, conditionValue, userInput);
    default:
      // Report unknown node subtypes to Sentry
      Sentry.captureMessage('Unknown node subtype in conditional logic', {
        level: 'warning',
        tags: {
          unknown_node_subtype: true,
          conditional_logic_error: true,
        },
        extra: {
          nodeSubtype: sourceNode.nodeSubtype,
          nodeId: sourceNode.id,
          conditionOperator: operator,
        },
      });

      return false;
  }
};

const evaluateMultipleChoiceCondition = (
  sourceNode: FormNode,
  conditionValue: string,
  userInput: string
): boolean => {
  // For multiple choice, check if the value matches the option at the specified index
  const optionIndex = parseInt(conditionValue);
  if (!isNaN(optionIndex) && sourceNode.options && sourceNode.options[optionIndex]) {
    return sourceNode.options[optionIndex] === userInput;
  }
  // Fallback to direct comparison
  return conditionValue === userInput;
};

const evaluateNumericCondition = (
  conditionValue: string,
  operator: string,
  userInput: string
): boolean => {
  const numValue = parseInt(conditionValue);
  const numUserInput = parseInt(userInput);
  if (isNaN(numValue) || isNaN(numUserInput)) return false;

  switch (operator) {
    case 'lt':
      return numValue > numUserInput;
    case 'gt':
      return numValue < numUserInput;
    case 'eq':
      return numValue === numUserInput;
    default:
      return false;
  }
};

const evaluateDateCondition = (
  conditionValue: string,
  operator: string,
  userInput: string
): boolean => {
  const [quantity, unit, anchor] = conditionValue.split('_');
  const userInputDate = new Date(userInput);
  if (isNaN(userInputDate.getTime())) return false;
  const today = new Date();
  let dateFunc: (date: number | Date, amount: number) => Date;

  switch (unit) {
    case 'days':
      dateFunc = anchor === 'ago' ? subDays : addDays;
      break;
    case 'weeks':
      dateFunc = anchor === 'ago' ? subWeeks : addWeeks;
      break;
    case 'months':
      dateFunc = anchor === 'ago' ? subMonths : addMonths;
      break;
    case 'years':
      dateFunc = anchor === 'ago' ? subYears : addYears;
      break;
    default:
      return false;
  }

  const quantityNum = parseInt(quantity);
  if (isNaN(quantityNum)) return false;
  const comparisonDate = dateFunc(today, quantityNum);

  switch (operator) {
    case 'lt':
      return comparisonDate < userInputDate;
    case 'gt':
      return comparisonDate > userInputDate;
    case 'eq':
      return comparisonDate.getTime() === userInputDate.getTime();
    default:
      return false;
  }
};
